import React from 'react';
import MovementBadge from './MovementBadge';
import { UserSwitch } from '@phosphor-icons/react';

// Renders ALL courts for the active week. Anyone with access to the
// session detail page can see this — registered or not.
//
// If `myProfileId` is provided AND matches a slot, that court gets a
// highlighted border + "You're on this court" pill, and the player's row
// is bolded inside the court. A "Find Sub" button appears next to that
// player's own row (calls `onFindSubClick` which switches to the
// Find Sub tab).
//
// `week` is the response shape from GET /event-sessions/{id}/weeks/{n}.
export default function AllCourtsView({ week, myProfileId, onFindSubClick }) {
  if (!week || !week.courts || week.courts.length === 0) {
    return (
      <div
        className="bg-[#fbfaf8] border border-[#e3dcd1] rounded-2xl p-4 text-center text-sm text-[#6a766e]"
        data-testid="no-courts-yet"
      >
        Courts for Week {week?.week?.week_number ?? '—'} haven't been set yet. Check back soon.
      </div>
    );
  }

  // Resolve which court (if any) contains the calling player.
  let myCourtId = null;
  if (myProfileId) {
    for (const c of week.courts) {
      for (const s of c.slots) {
        if (s.profile_id === myProfileId) {
          myCourtId = c.id;
          break;
        }
      }
      if (myCourtId) break;
    }
  }

  return (
    <div className="space-y-4" data-testid="all-courts-view">
      <div className="text-[11px] uppercase tracking-wider text-[#6a766e]">
        Assignments — Week {week.week.week_number}
      </div>
      {week.courts.map((c) => (
        <CourtCard
          key={c.id}
          court={c}
          isMyCourt={c.id === myCourtId}
          myProfileId={myProfileId}
          onFindSubClick={onFindSubClick}
        />
      ))}
    </div>
  );
}

function CourtCard({ court, isMyCourt, myProfileId, onFindSubClick }) {
  return (
    <section
      data-testid={`court-card-${court.court_number}`}
      className={`bg-[#fbfaf8] rounded-2xl p-4 space-y-3 transition-all ${
        isMyCourt
          ? 'border-2 border-[#015e3f] shadow-sm'
          : 'border border-[#e3dcd1]'
      }`}
    >
      <header className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="font-serif text-2xl text-[#013523]">
            Court {court.court_number}
          </div>
          {isMyCourt && (
            <span
              data-testid="my-court-pill"
              className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#015e3f] text-white"
            >
              You're here
            </span>
          )}
        </div>
      </header>
      <ul className="divide-y divide-[#e3dcd1]">
        {court.slots.map((s) => {
          const isMe = s.profile_id === myProfileId;
          return (
            <li
              key={s.id}
              data-testid={`court-slot-${s.id}`}
              className={`py-2 flex items-center justify-between gap-2 ${
                isMe ? 'font-medium text-[#015e3f]' : 'text-[#013523]'
              }`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 truncate" data-testid={`court-slot-name-${s.id}`}>
                  {isMe && <span className="text-[#015e3f]">→</span>}
                  <span className={s.no_show ? 'line-through text-[#6a766e]' : ''}>
                    {s.display_name || s.player_name}
                  </span>
                  {isMe && <span className="text-[11px] text-[#6a766e]">(you)</span>}
                  {s.movement && <MovementBadge movement={s.movement} label={`Last week: ${s.movement}`} />}
                </div>
                {/* Sub annotation — shows on Assignments + History when a sub
                    played the slot. e.g. "Sub: John Smith for Aaron Arnett" */}
                {s.is_substitute && s.sub_player_name && (
                  <div
                    className="mt-0.5 text-[11px] text-[#6a766e]"
                    data-testid={`court-slot-sub-note-${s.id}`}
                  >
                    Sub: <span className="text-[#013523]">{s.sub_player_name}</span> for {s.player_name}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {isMe && onFindSubClick && (
                  <button
                    type="button"
                    onClick={onFindSubClick}
                    className="text-[10px] uppercase tracking-wider px-2 py-1 rounded-md bg-white border border-[#015e3f] text-[#015e3f] flex items-center gap-1 hover:bg-[#015e3f] hover:text-white transition-colors"
                    data-testid={`find-sub-btn-${s.id}`}
                  >
                    <UserSwitch size={11} /> Find Sub
                  </button>
                )}
                <span className="text-[11px] text-[#6a766e]">
                  {s.points != null ? `${s.points} pts` : 'Not yet scored'}
                </span>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
